/**
 * Acender a borda onde ela deve clicar.
 *
 * ── Por que não é o holofote do tour ──────────────────────────────────────
 *
 * `TutorialModal` faz algo parecido e bem mais pesado: escurece a tela, mede
 * a posição, espera o scroll estabilizar e ancora um card com "Próximo". É um
 * tour — ele toma a tela porque está conduzindo uma sequência.
 *
 * A faixa não conduz nada. Ela diz uma frase e aponta um lugar; tomar a tela
 * para isso seria um modal por dica, e a pessoa fecharia 23 modais. Aqui a
 * borda pulsa e a tela continua dela.
 *
 * ── O que É compartilhado, e por isso saiu do tour ────────────────────────
 *
 * Uma seção colapsada não mostra o alvo, e apontar para dentro dela ilumina
 * uma dobra fechada. A regra de abrir é a mesma nos dois casos — e duas
 * cópias divergiriam no dia em que a classe do botão mudasse. Por isso
 * `expandirSeColapsada` vive aqui e o tour a importa.
 */

/**
 * Abre a seção colapsada que contém (ou é) o elemento.
 *
 * Devolve `true` quando abriu algo — quem chama precisa saber, porque a
 * animação de abrir muda a posição e o scroll tem que esperar.
 */
export const expandirSeColapsada = (el: HTMLElement): boolean => {
  const secao = el.classList.contains('ss-collapsed')
    ? el
    : el.closest<HTMLElement>('.ss-collapsed');
  if (!secao) return false;

  const btn = secao.querySelector<HTMLButtonElement>(
    '.ss-collapse-action, .ss-collapse-header',
  );
  if (!btn) return false;
  btn.click();
  return true;
};

/** Quanto tempo a borda fica pulsando. */
export const DURACAO_PULSO_MS = 4200;
/** O tempo da animação de abrir a seção, antes de rolar até ela. */
const ESPERA_EXPANSAO_MS = 300;

export const CLASSE_PULSO = 'dica-pulso';

export interface OpcoesDeRevelar {
  duracaoMs?: number;
  doc?: Document;
  /** Injetável para o teste: rolar de verdade não acontece no jsdom. */
  rolar?: (el: HTMLElement) => void;
}

const rolarPadrao = (el: HTMLElement) =>
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });

/**
 * Rola até o alvo e pulsa a borda dele.
 *
 * Devolve a função que cancela — a faixa chama no desmonte, senão a classe
 * fica pendurada num elemento que ninguém está mais olhando, e a próxima
 * dica encontra dois pulsos ao mesmo tempo.
 *
 * Alvo inexistente devolve um cancelador que não faz nada, em vez de lançar:
 * no celular o formulário completo não é montado, e uma dica que aponta para
 * ele não pode derrubar a tela.
 */
export const revelarAlvo = (
  id: string, opts: OpcoesDeRevelar = {},
): (() => void) => {
  const doc = opts.doc ?? (typeof document !== 'undefined' ? document : null);
  if (!doc) return () => {};

  const el = doc.getElementById(id);
  if (!el) return () => {};

  const rolar = opts.rolar ?? rolarPadrao;
  const duracao = opts.duracaoMs ?? DURACAO_PULSO_MS;

  // Um pulso por vez. Sem isto, clicar em duas dicas seguidas deixa a borda
  // acesa em dois lugares e nenhum dos dois é "onde clicar".
  doc.querySelectorAll('.' + CLASSE_PULSO)
    .forEach(x => x.classList.remove(CLASSE_PULSO));

  const abriu = expandirSeColapsada(el);

  const timerRolagem = setTimeout(
    () => rolar(el), abriu ? ESPERA_EXPANSAO_MS : 0,
  );

  el.classList.add(CLASSE_PULSO);
  const timerPulso = setTimeout(() => el.classList.remove(CLASSE_PULSO), duracao);

  return () => {
    clearTimeout(timerRolagem);
    clearTimeout(timerPulso);
    el.classList.remove(CLASSE_PULSO);
  };
};
